/**
 * Investor-Portal — GuV-Page UGB-konform (#1146 v2.499.0).
 * Hierarchie Pos 1-17 + Subtotals (Rohertrag/EBITDA/EBIT/Finanzergebnis/Ergebnis vor Steuern)
 * + Prozent-Zeilen. Drill-Down zu /investor/aufwendungen für Pos 4, 5, 6, 8.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PeriodToggle, { buildPeriodQuery } from './components/PeriodToggle';

const API = import.meta.env.VITE_API_URL || '';

function fmtEur(v) {
  if (v === null || v === undefined) return '–';
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
}
function fmtPct(v) {
  if (v === null || v === undefined) return '–';
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(1)} %`;
}
function fmtPctOnly(v) {
  if (v === null || v === undefined) return '–';
  return `${v.toFixed(1)} %`;
}
function trendIcon(trend) {
  if (trend === 'up_good') return { glyph: '↗', color: '#10B981' };
  if (trend === 'down_bad') return { glyph: '↘', color: '#EF4444' };
  return { glyph: '→', color: '#9CA3AF' };
}
function deltaColor(line) {
  if (line.trend === 'up_good') return 'text-emerald-600';
  if (line.trend === 'down_bad') return 'text-red-600';
  return 'text-[#6B7280]';
}

export default function InvestorGuv() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentYear = new Date().getFullYear();
  const [periodState, setPeriodState] = useState({
    period: 'ytd',
    year: currentYear,
    month: new Date().getMonth() + 1,
    quarter: Math.ceil((new Date().getMonth() + 1) / 3),
    fromDate: null,
    toDate: null,
  });
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEmpty, setShowEmpty] = useState(false);

  const periodQuery = useMemo(() => buildPeriodQuery(periodState), [periodState]);

  useEffect(() => {
    let abort = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const q = new URLSearchParams(periodQuery);
        if (showEmpty) q.set('show_empty', 'true');
        const res = await fetch(`${API}/api/investor/guv-hierarchy?${q.toString()}`, { credentials: 'include' });
        const data = await res.json();
        if (abort) return;
        if (!res.ok) {
          setError(data.error || `HTTP ${res.status}`);
          setLines([]);
        } else {
          setLines(data.lines || []);
        }
      } catch (e) {
        if (!abort) setError(e.message);
      } finally {
        if (!abort) setLoading(false);
      }
    }
    load();
    return () => { abort = true; };
  }, [periodQuery, showEmpty]);

  function onDrill(line) {
    const code = line.position_code;
    const q = new URLSearchParams(periodQuery);
    q.set('focus', code);
    navigate(`/investor/aufwendungen?${q.toString()}`);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A1A2E]">Gewinn- und Verlustrechnung</h1>
          <div className="text-sm text-[#6B7280] mt-1">UGB-Bericht § 231 (AT) — Positionen 1 bis 17</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PeriodToggle
            period={periodState.period}
            year={periodState.year}
            month={periodState.month}
            quarter={periodState.quarter}
            fromDate={periodState.fromDate}
            toDate={periodState.toDate}
            onChange={setPeriodState}
          />
          <label className="flex items-center gap-1.5 text-xs text-[#4B5563] cursor-pointer bg-white border border-[#E5E7EB] rounded-md px-2 py-1.5">
            <input type="checkbox" checked={showEmpty} onChange={e => setShowEmpty(e.target.checked)} className="accent-[#338edc]" />
            Alle UGB-Positionen anzeigen
          </label>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg">
          Fehler: {error}
        </div>
      )}

      <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-12">Pos.</th>
                <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Bezeichnung</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-28">Plan</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-28">IST</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-28">Δ abs</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-20">Δ %</th>
                <th className="text-center px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-14">Trend</th>
                <th className="text-center px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-14"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="px-3 py-12 text-center text-[#9CA3AF]">Lade…</td></tr>
              )}
              {!loading && lines.length === 0 && !error && (
                <tr><td colSpan={8} className="px-3 py-12 text-center text-[#9CA3AF]">Keine Daten</td></tr>
              )}
              {!loading && lines.map((l, idx) => {
                const t = trendIcon(l.trend);
                const isSubtotal = l.is_subtotal;
                const isPercent = l.is_percent;
                const indent = l.hierarchy_level === 1 ? 'pl-10' : 'pl-3';
                const fmtFn = isPercent ? fmtPctOnly : fmtEur;
                const labelClass = isSubtotal
                  ? 'font-bold text-[#1A1A2E]'
                  : isPercent
                    ? 'italic text-[#6B7280]'
                    : l.hierarchy_level === 1
                      ? 'text-[#4B5563]'
                      : 'text-[#1A1A2E]';
                const rowClass = isSubtotal
                  ? 'border-t-2 border-[#1A1A2E] bg-[#F8F9FC]/50'
                  : isPercent
                    ? 'bg-[#FAFBFC]'
                    : 'border-t border-[#F3F4F6] hover:bg-[#FAFBFC]';
                const posLabel = !isSubtotal && !isPercent && l.hierarchy_level === 0
                  ? (l.position_name.match(/^(\d+)\./) || [, ''])[1]
                  : (l.hierarchy_level === 1 ? '' : '');
                return (
                  <tr key={`${l.position_code}-${idx}`} className={rowClass}>
                    <td className={`px-3 py-1.5 text-[11px] text-[#9CA3AF] ${isSubtotal ? 'font-bold' : ''}`}>
                      {posLabel}
                    </td>
                    <td className={`py-1.5 ${indent} ${labelClass}`}>
                      <span className={l.hierarchy_level === 1 ? 'text-xs' : ''}>
                        {isSubtotal ? l.position_name : (
                          l.hierarchy_level === 0
                            ? l.position_name.replace(/^\d+\.\s*/, '')
                            : l.position_name.replace(/^[a-z]\.\s*/, '')
                        )}
                      </span>
                    </td>
                    <td className={`px-3 py-1.5 text-right tabular-nums ${labelClass}`}>{fmtFn(l.plan_eur)}</td>
                    <td className={`px-3 py-1.5 text-right tabular-nums ${labelClass}`}>{fmtFn(l.ist_eur)}</td>
                    <td className={`px-3 py-1.5 text-right tabular-nums ${deltaColor(l)} ${isSubtotal ? 'font-bold' : ''}`}>
                      {l.delta_abs !== null ? fmtFn(l.delta_abs) : '–'}
                    </td>
                    <td className={`px-3 py-1.5 text-right tabular-nums ${deltaColor(l)} ${isSubtotal ? 'font-bold' : ''}`}>
                      {fmtPct(l.delta_pct)}
                    </td>
                    <td className="px-3 py-1.5 text-center text-base" style={{ color: t.color }}>{t.glyph}</td>
                    <td className="px-3 py-1.5 text-center">
                      {l.has_drill && (
                        <button
                          onClick={() => onDrill(l)}
                          title="Drill-Down zu Aufwendungen"
                          className="w-6 h-6 inline-flex items-center justify-center rounded-md bg-[#338edc]/10 text-[#338edc] hover:bg-[#338edc]/20 text-xs"
                        >
                          ⌕
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-xs text-[#9CA3AF]">
        Quelle: Odoo UGB-Bericht § 231 (AT) — Daten ab Buchungsstand letzter Odoo-Sync.
        Plan-Werte aus Investor-Excel. Sub-Positionen ohne expliziten Plan = 0.
      </div>
    </div>
  );
}
